import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { ErrorNote, useMe } from '../App.js';
import { api } from '../api.js';
import { useI18n } from '../i18n.js';
import { InviteDialog, MembersTab } from './org/Members.js';
import { ProjectsTab } from './org/Projects.js';
import { SettingsTab } from './org/Settings.js';
import { formatKennitala, peopleRows } from './orgParts.js';
import { NewProjectDialog } from './project/ProjectDialogs.js';

/**
 * An organization's page (owner's layout, 2026-11-01): a header with the organization's facts and the action for
 * the open tab, then Verkefni | Meðlimir | Stillingar.
 */
export function OrgPage() {
  const { t } = useI18n();
  const { org } = useParams() as { org: string };
  const me = useMe();
  const loc = useLocation();
  const members = useQuery({ queryKey: ['org', org], queryFn: () => api.orgMembers(org) });
  const projects = useQuery({ queryKey: ['orgProjects', org], queryFn: () => api.orgProjects(org) });
  const myRole = me.data?.orgs.find((o) => o.slug === org)?.role;
  const superadmin = Boolean(me.data?.user.isSuperadmin);
  const isAdmin = superadmin || myRole === 'owner' || myRole === 'admin';
  const isOwner = superadmin || myRole === 'owner';
  const canCreate = superadmin;
  const invitations = useQuery({
    queryKey: ['invitations', org],
    queryFn: () => api.invitations(org),
    enabled: isAdmin,
  });
  const [dialog, setDialog] = useState<'invite' | 'project' | null>(null);
  // On a narrow screen the tabs scroll sideways: keep the current one in view.
  useEffect(() => {
    document.querySelector('.tabs a.active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [loc.pathname]);

  if (members.error)
    return (
      <div className="page">
        <ErrorNote error={members.error} />
      </div>
    );
  if (!members.data) return <div className="center muted">{t('loading')}</div>;
  const o = members.data.org;
  const base = `/orgs/${org}`;
  const tab = loc.pathname.slice(base.length + 1).split('/')[0] || 'projects';
  const peopleCount = peopleRows(members.data.members, invitations.data ?? []).length;
  const facts = [o.kennitala ? `${t('kt')} ${formatKennitala(o.kennitala)}` : null, o.address || null].filter(
    (x): x is string => Boolean(x),
  );

  const action =
    tab === 'projects' && canCreate ? (
      <button className="primary" onClick={() => setDialog('project')}>
        <Plus />
        {t('newProject')}
      </button>
    ) : tab === 'members' && isAdmin ? (
      <button className="primary" onClick={() => setDialog('invite')}>
        <Plus />
        {t('inviteMember')}
      </button>
    ) : null;

  return (
    <div className="page">
      <nav className="crumbs" aria-label={t('organizations')}>
        <Link to="/orgs">{t('organizations')}</Link>
        <span aria-hidden="true">/</span>
        <span>{o.name}</span>
      </nav>
      <header className="org-head">
        <div className="org-id">
          <div className="org-mark" aria-hidden="true">
            {o.name.slice(0, 1).toUpperCase()}
          </div>
          <div className="org-id-text">
            <h1 className="org-title">{o.name}</h1>
            {(facts.length > 0 || members.data.domains.length > 0) && (
              <div className="org-facts">
                {facts.map((f) => (
                  <span key={f}>{f}</span>
                ))}
                {members.data.domains.map((d) => (
                  <span key={d} className="org-domain" title={t('verifiedDomains')}>
                    <Check />
                    {d}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        {action && <div className="org-actions">{action}</div>}
      </header>

      <nav className="tabs" aria-label={o.name}>
        <NavLink to={`${base}/projects`}>
          {t('projects')}
          {projects.data && <span className="count">{projects.data.length}</span>}
        </NavLink>
        <NavLink to={`${base}/members`}>
          {t('members')}
          <span className="count">{peopleCount}</span>
        </NavLink>
        <NavLink to={`${base}/settings`}>{t('settings')}</NavLink>
      </nav>

      <Routes>
        <Route index element={<Navigate to={`${base}/projects`} replace />} />
        <Route
          path="projects"
          element={
            <ProjectsTab query={projects} onCreate={canCreate ? () => setDialog('project') : undefined} />
          }
        />
        <Route
          path="members"
          element={
            <MembersTab
              org={o}
              members={members.data.members}
              invitations={invitations.data ?? []}
              isAdmin={isAdmin}
              isOwner={isOwner}
              meId={me.data?.user.id}
              projectCount={projects.data?.length ?? 0}
              onInvite={isAdmin ? () => setDialog('invite') : undefined}
            />
          }
        />
        <Route
          path="settings"
          element={
            <SettingsTab org={o} domains={members.data.domains} canEdit={isAdmin} superadmin={superadmin} />
          }
        />
        <Route path="*" element={<Navigate to={`${base}/projects`} replace />} />
      </Routes>

      {dialog === 'invite' && (
        <InviteDialog org={o} projects={projects.data ?? []} onClose={() => setDialog(null)} />
      )}
      {dialog === 'project' && <NewProjectDialog org={org} onClose={() => setDialog(null)} />}
    </div>
  );
}

function Plus() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function Check() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
